import { useEffect, useState } from 'react'

/** What the UI says once the confirm deadline has passed. */
export const STAGING_EXPIRED_LABEL = 'expired'

/**
 * Human countdown to the staged-archive confirm deadline. At or below zero it
 * reports expired — the UI is allowed to be a hair conservative here, since
 * erring toward "expired" can only over-warn, never send someone to a Confirm
 * that 410s.
 */
export function formatTimeRemaining(msRemaining: number): string {
  if (msRemaining <= 0) return STAGING_EXPIRED_LABEL

  const totalMinutes = Math.floor(msRemaining / 60_000)
  if (totalMinutes < 1) return 'under a minute'

  const hours = Math.floor(totalMinutes / 60)
  const minutes = totalMinutes % 60
  return hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`
}

/**
 * Milliseconds left until an absolute ISO deadline, re-rendering once a minute.
 * The deadline travels as an absolute server instant rather than a remaining-ms
 * snapshot — that trades invisible clock skew for immunity to caching and tab
 * sleep. Null (no interval at all) when there's no deadline.
 *
 * Only Review uses this: it's the surface people leave open for an hour, so a
 * frozen number there would be a lie. The menu is transient and computes its
 * text once at render.
 */
export function useTimeRemaining(expiresAt?: string | null): number | null {
  const [now, setNow] = useState(() => Date.now())

  useEffect(() => {
    if (!expiresAt) return
    setNow(Date.now())
    const interval = setInterval(() => setNow(Date.now()), 60_000)
    return () => clearInterval(interval)
  }, [expiresAt])

  if (!expiresAt) return null
  return new Date(expiresAt).getTime() - now
}
